import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { AgencyMode, TransitGroup, TransitPrediction, TransitStop } from '../types';

const INITIAL_VISIBLE = 3;
const SCROLL_TOP_PAD = 40;
const SCROLL_BOTTOM_PAD = 60;

const STOP_TYPE_ORDER: Record<TransitStop['type'], number> = {
  masstransit: 0,
  docked: 1,
  dockless: 2,
};

function getModeLabel(mode: AgencyMode): string {
  switch (mode) {
    case 'bus':
      return 'Bus';
    case 'bikeshare':
      return 'Bike Share';
    case 'rail':
      return 'Rail';
    case 'streetcar':
      return 'Streetcar';
    case 'subway':
      return 'Subway';
    case 'train':
      return 'Train';
    case 'shuttle':
      return 'Shuttle';
    case 'floating':
      return 'Rideshare';
    case 'scootershare':
      return 'Scooter';
    default:
      return 'Transit';
  }
}

function getNextArrival(stop: TransitStop): TransitPrediction | null {
  if (!stop.predictions || stop.predictions.length === 0) return null;
  return stop.predictions[0] ?? null;
}

function formatArrival(prediction: TransitPrediction | null): string | null {
  if (!prediction) return null;
  if (prediction.minutesAway === 0) return 'Now';
  if (prediction.minutesAway === 1) return '1 min';
  return `${prediction.minutesAway} min`;
}

function ChevronIcon({ open }: { open: boolean }): ReactNode {
  return (
    <svg
      className={`transit-group-chevron${open ? ' open' : ''}`}
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
    >
      <path
        d="M4.5 5.5L7 8L9.5 5.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface TransitGroupSectionProps {
  group: TransitGroup;
  selectedStopId: string | null;
  onStopSelect: (stop: TransitStop) => void;
  autoExpand: boolean;
}

function TransitGroupSection({
  group,
  selectedStopId,
  onStopSelect,
  autoExpand,
}: TransitGroupSectionProps): ReactNode {
  const [collapsed, setCollapsed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const hasMore = group.stops.length > INITIAL_VISIBLE;

  // Auto-expand and auto-uncollapse synchronously during render so the DOM
  // has the row before the parent's scroll-into-view effect fires.
  if (autoExpand && !expanded) setExpanded(true);
  if (collapsed && autoExpand) setCollapsed(false);

  const visibleStops = expanded ? group.stops : group.stops.slice(0, INITIAL_VISIBLE);
  const hiddenCount = group.stops.length - INITIAL_VISIBLE;

  return (
    <div className={`transit-group${collapsed ? ' collapsed' : ''}`}>
      <button
        type="button"
        className="transit-group-header"
        onClick={() => setCollapsed(!collapsed)}
      >
        <ChevronIcon open={!collapsed} />
        <span className="transit-group-name">{group.agencyName}</span>
        <span className="transit-group-mode">
          {getModeLabel(group.agencyMode)}
          <span className="transit-group-count">{group.stops.length}</span>
        </span>
        {(group.logoUrl || group.modeIconUrl) && (
          <img
            className="transit-group-logo"
            src={group.logoUrl ?? group.modeIconUrl ?? ''}
            alt=""
          />
        )}
      </button>
      {!collapsed && (
        <>
          <div className="transit-stop-list">
            {visibleStops.map((stop) => (
              <StopRow
                key={stop.id}
                stop={stop}
                group={group}
                selected={selectedStopId === stop.id}
                onSelect={onStopSelect}
              />
            ))}
          </div>
          {hasMore && (
            <button
              type="button"
              className="transit-see-more"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'Show less' : `+${hiddenCount} more`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

interface StopRowProps {
  stop: TransitStop;
  group: TransitGroup;
  selected: boolean;
  onSelect: (stop: TransitStop) => void;
}

function StopRow({ stop, group, selected, onSelect }: StopRowProps): ReactNode {
  const next = stop.type === 'masstransit' ? getNextArrival(stop) : null;
  const arrivalLabel = formatArrival(next);

  let arrivalNode: ReactNode = null;
  if (stop.type === 'masstransit') {
    if (arrivalLabel && next) {
      const urgency = next.minutesAway <= 1 ? ' arriving' : next.minutesAway <= 5 ? ' soon' : '';
      arrivalNode = <span className={`transit-stop-arrival${urgency}`}>{arrivalLabel}</span>;
    } else if (stop.walkMinutes != null && stop.walkMinutes >= 0) {
      arrivalNode = <span className="transit-stop-meta">{stop.walkMinutes}m walk</span>;
    }
  } else if (stop.walkMinutes != null && stop.walkMinutes >= 0) {
    arrivalNode = (
      <span className="transit-stop-meta">
        {stop.type === 'dockless' ? `${stop.walkMinutes}m ETA` : `${stop.walkMinutes}m walk`}
      </span>
    );
  }

  return (
    <button
      type="button"
      data-list-stop-id={stop.id}
      className={`transit-stop-row${selected ? ' selected' : ''}`}
      onClick={() => onSelect(stop)}
    >
      <div className="transit-stop-info">
        <span className="transit-stop-name">{stop.name}</span>
        {next && (
          <span className="transit-stop-route-detail">
            {next.shortRoute && (
              <span
                className="transit-route-badge"
                style={{
                  backgroundColor: next.routeColor ?? group.brandColor,
                  color: next.textColor ?? '#fff',
                }}
              >
                {next.shortRoute}
              </span>
            )}
            {next.destination && (
              <span className="transit-stop-destination">→ {next.destination}</span>
            )}
          </span>
        )}
      </div>
      {arrivalNode}
    </button>
  );
}

function SupportPrompt(): ReactNode {
  return (
    <div className="transit-support-prompt">
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="8" cy="8" r="7" />
        <path d="M5.5 6a2.5 2.5 0 0 1 5 0c0 1.5-2.5 1.5-2.5 3" />
        <circle cx="8" cy="12" r="0.5" fill="currentColor" stroke="none" />
      </svg>
      <span>
        Need more transit data? <a href="mailto:support@mappedin.com">Contact Mappedin support</a>{' '}
        to request additional coverage for this location.
      </span>
    </div>
  );
}

function BusEmptyIcon(): ReactNode {
  return (
    <svg className="transit-empty-icon" width="48" height="48" viewBox="0 0 48 48" fill="none">
      <rect x="4" y="12" width="40" height="28" rx="6" stroke="currentColor" strokeWidth="2" />
      <rect
        x="12"
        y="18"
        width="10"
        height="6"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.5"
      />
      <rect
        x="26"
        y="18"
        width="10"
        height="6"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.5"
      />
      <circle cx="16" cy="34" r="3" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="32" cy="34" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="M4 28h40" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
      <path
        d="M18 8h12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.4"
      />
    </svg>
  );
}

interface TransitListProps {
  stops: TransitStop[];
  selectedStopId: string | null;
  onStopSelect: (stop: TransitStop) => void;
  loading: boolean;
  error: string | null;
}

export function TransitList({
  stops,
  selectedStopId,
  onStopSelect,
  loading,
  error,
}: TransitListProps): ReactNode {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!selectedStopId || !scrollRef.current) return undefined;
    const raf = requestAnimationFrame(() => {
      const container = scrollRef.current;
      if (!container) return;
      const el = container.querySelector(`[data-list-stop-id="${CSS.escape(selectedStopId)}"]`);
      if (!(el instanceof HTMLElement)) return;
      const cRect = container.getBoundingClientRect();
      const eRect = el.getBoundingClientRect();
      if (eRect.top < cRect.top + SCROLL_TOP_PAD) {
        container.scrollBy({ top: eRect.top - cRect.top - SCROLL_TOP_PAD, behavior: 'smooth' });
      } else if (eRect.bottom > cRect.bottom - SCROLL_BOTTOM_PAD) {
        container.scrollBy({
          top: eRect.bottom - cRect.bottom + SCROLL_BOTTOM_PAD,
          behavior: 'smooth',
        });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [selectedStopId]);

  const grouped = useMemo<TransitGroup[]>(() => {
    const map = new Map<string | number, TransitGroup>();
    for (const stop of stops) {
      let group = map.get(stop.agencyId);
      if (!group) {
        group = {
          agencyId: stop.agencyId,
          agencyName: stop.agencyName,
          agencyMode: stop.agencyMode,
          brandColor: stop.brandColor,
          logoUrl: stop.logoUrl,
          modeIconUrl: stop.modeIconUrl,
          stops: [],
        };
        map.set(stop.agencyId, group);
      }
      group.stops.push(stop);
    }
    return Array.from(map.values()).sort((a, b) => {
      const aType = a.stops[0]?.type;
      const bType = b.stops[0]?.type;
      const aOrder = aType ? STOP_TYPE_ORDER[aType] : 3;
      const bOrder = bType ? STOP_TYPE_ORDER[bType] : 3;
      return aOrder - bOrder;
    });
  }, [stops]);

  if (loading) {
    return (
      <div className="transit-list">
        <div className="transit-list-loading">
          <div className="spinner" />
          <span>Finding nearby transit...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="transit-list">
        <div className="transit-list-empty">
          <BusEmptyIcon />
          <span className="transit-empty-title">Transit data unavailable</span>
          <span className="transit-empty-desc">
            We couldn&apos;t load nearby transit for this venue right now.
          </span>
          <SupportPrompt />
        </div>
      </div>
    );
  }

  if (stops.length === 0) {
    return (
      <div className="transit-list">
        <div className="transit-list-empty">
          <BusEmptyIcon />
          <span className="transit-empty-title">No transit found</span>
          <span className="transit-empty-desc">
            No nearby transit options were found for this venue address.
          </span>
          <SupportPrompt />
        </div>
      </div>
    );
  }

  return (
    <div className="transit-list">
      <div className="transit-list-header">
        <h2>Nearby Transit</h2>
        <button
          type="button"
          className="transit-list-action"
          onClick={() => setShowConfirmation(true)}
        >
          Request to Add Feed
        </button>
      </div>
      {showConfirmation && (
        <div className="transit-confirmation">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="9" stroke="#34d399" strokeWidth="1.5" />
            <path
              d="M6.5 10.5l2 2 5-5"
              stroke="#34d399"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>
            Mappedin has been notified to add this data source to all your available apps.
          </span>
          <button
            type="button"
            className="transit-confirmation-dismiss"
            onClick={() => setShowConfirmation(false)}
          >
            Dismiss
          </button>
        </div>
      )}
      <div className="transit-list-scroll" ref={scrollRef}>
        {grouped.map((group) => {
          const selectedIndex = selectedStopId
            ? group.stops.findIndex((s) => s.id === selectedStopId)
            : -1;
          const needsExpand = selectedIndex >= INITIAL_VISIBLE;
          return (
            <TransitGroupSection
              key={group.agencyId}
              group={group}
              selectedStopId={selectedStopId}
              onStopSelect={onStopSelect}
              autoExpand={needsExpand}
            />
          );
        })}
        {stops.length > 0 && stops.length <= 5 && <SupportPrompt />}
      </div>
    </div>
  );
}
